import { apiClient, HttpError } from '@littlebird/auth-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { isSafeBrowserStreamUrl } from './browser-stream-url';

export const browserStreamKey = (chatId: string) =>
  ['browser-stream', chatId] as const;

const streamSchema = z
  .object({ stream_url: z.string(), mode: z.literal('view_only') })
  .refine((value) => isSafeBrowserStreamUrl(value.stream_url), {
    path: ['stream_url'],
    message: 'Unsafe browser stream URL',
  });

export const shouldRetryBrowserStreamQuery = (
  failureCount: number,
  error: unknown
) =>
  failureCount < (error instanceof HttpError && error.status === 404 ? 8 : 2);

export const browserStreamRetryDelay = (attempt: number) =>
  Math.min(500 * 2 ** attempt, 4_000);

export const BROWSER_STREAM_REFETCH_INTERVAL_MS = 5_000;

export const useBrowserStreamQuery = (
  chatId: string | undefined,
  isVisible: boolean,
  isAgentBusy: boolean,
  sessionRevision?: string
) => {
  const queryClient = useQueryClient();
  const previousVisible = useRef(isVisible);
  const previousAgentBusy = useRef(isAgentBusy);
  const query = useQuery({
    queryKey: [...browserStreamKey(chatId ?? ''), sessionRevision],
    queryFn: async ({ signal }) =>
      streamSchema.parse(
        await apiClient.get(`chat/${chatId}/browser_stream`, {
          signal,
          emitHttpErrorEvent: false,
        })
      ),
    enabled: Boolean(chatId) && isVisible,
    retry: shouldRetryBrowserStreamQuery,
    retryDelay: browserStreamRetryDelay,
    staleTime: 90_000,
    gcTime: 0,
    refetchInterval:
      isVisible && isAgentBusy ? BROWSER_STREAM_REFETCH_INTERVAL_MS : false,
  });
  const { refetch } = query;

  useEffect(() => {
    // Clear paused viewers' URLs so resuming never shows a stale stream
    if (!isVisible)
      void queryClient.resetQueries({
        queryKey: browserStreamKey(chatId ?? ''),
      });
  }, [chatId, isVisible, queryClient]);

  useEffect(() => {
    const reopened = isVisible && !previousVisible.current;
    const turnEnded = isVisible && previousAgentBusy.current && !isAgentBusy;
    previousVisible.current = isVisible;
    previousAgentBusy.current = isAgentBusy;
    if (chatId && (reopened || turnEnded)) void refetch();
  }, [chatId, isVisible, isAgentBusy, refetch]);

  return query;
};
